import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, RefreshCw, Download, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";

function fmtDate(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale === "en" ? "en-US" : "es-MX", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ManualCard() {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pdfLocale, setPdfLocale] = useState("en");
  const pollRef = useRef(null);

  const STEP_LABELS = {
    seeding: t("manual.step.seeding"),
    capturing: t("manual.step.capturing"),
    rendering: t("manual.step.rendering"),
    cleanup: t("manual.step.cleanup"),
  };

  const load = async () => {
    try {
      const r = await api.get("/admin/manual/status");
      setInfo(r.data);
      // If current build has a locale, reflect it so the user sees what was generated last.
      const lastLocale = r.data?.last_success?.locale || r.data?.in_progress?.locale;
      if (lastLocale === "en" || lastLocale === "es") setPdfLocale(lastLocale);
      if (r.data.in_progress) {
        if (!pollRef.current) {
          pollRef.current = setInterval(load, 4000);
        }
      } else if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) {
      // ignore — likely not admin
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line
  }, []);

  const rebuild = async () => {
    if (!window.confirm(t("manual.confirm_rebuild", { lang: pdfLocale === "en" ? "English" : "Español" }))) return;
    setBusy(true);
    try {
      await api.post("/admin/manual/rebuild", { locale: pdfLocale });
      toast.success(t("manual.rebuild_started"));
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("manual.rebuild_error"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!info) return null;

  const inProg = info.in_progress;
  const last = info.last_success;
  const lastErr = info.last_error;
  const lastLocale = last?.locale || inProg?.locale;

  return (
    <div
      className="bg-[#F3F4F6] border border-[#E5E7EB] rounded-xl p-4 min-w-[290px] max-w-sm"
      data-testid="manual-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-md bg-[#1B2E7A]/10 flex items-center justify-center shrink-0">
          <BookOpen className="h-4 w-4 text-[#1B2E7A]" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">{t("manual.eyebrow")}</p>
          <p className="font-heading text-base text-[#1A1A1A] leading-snug">
            {t("manual.title")}
          </p>
          <p className="text-xs text-[#6B7280] mt-0.5">
            {t("manual.last_update")}: {fmtDate(last?.finished_at, uiLocale)}
            {lastLocale ? ` · ${lastLocale.toUpperCase()}` : ""}
          </p>
        </div>
      </div>

      {/* Language selector */}
      <div className="bg-white rounded-lg p-2.5 mb-2 border border-[#E5E7EB]" data-testid="manual-locale-picker">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] font-medium mb-1.5">
          {t("manual.locale_label")}
        </p>
        <div className="inline-flex rounded-md overflow-hidden border border-[#E5E7EB] w-full">
          <button
            type="button"
            onClick={() => setPdfLocale("en")}
            disabled={busy || !!inProg}
            className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
              pdfLocale === "en"
                ? "bg-[#1B2E7A] text-white"
                : "bg-white text-[#374151] hover:bg-[#FFFFFF]"
            }`}
            data-testid="manual-locale-en"
          >
            🇺🇸 English
          </button>
          <button
            type="button"
            onClick={() => setPdfLocale("es")}
            disabled={busy || !!inProg}
            className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
              pdfLocale === "es"
                ? "bg-[#1B2E7A] text-white"
                : "bg-white text-[#374151] hover:bg-[#FFFFFF]"
            }`}
            data-testid="manual-locale-es"
          >
            🇲🇽 Español
          </button>
        </div>
        <p className="text-[10px] text-[#6B7280] mt-1.5">
          {t("manual.locale_hint")}
        </p>
      </div>

      {inProg ? (
        <div className="bg-white rounded-lg p-3 mb-2" data-testid="manual-progress">
          <div className="flex items-center gap-2 mb-1.5">
            <Loader2 className="h-3.5 w-3.5 text-[#1B2E7A] animate-spin" />
            <p className="text-xs font-medium text-[#1A1A1A]">
              {STEP_LABELS[inProg.status] || inProg.status}
            </p>
          </div>
          <div className="h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1B2E7A] transition-all duration-700"
              style={{ width: `${((inProg.step || 0) / (inProg.total_steps || 4)) * 100}%` }}
            />
          </div>
          <p className="text-[10px] text-[#6B7280] mt-1.5">
            {t("manual.step_progress", { step: inProg.step || 0, total: inProg.total_steps || 4 })}
            {inProg.screenshots ? ` · ${t("manual.screenshots", { n: inProg.screenshots })}` : ""}
          </p>
        </div>
      ) : lastErr && (!last || lastErr.finished_at > (last.finished_at || "")) ? (
        <div className="bg-white border border-[#DC2626]/30 rounded-lg p-2.5 mb-2 text-xs text-[#DC2626]" data-testid="manual-error">
          <p className="inline-flex items-center gap-1.5 font-medium mb-0.5">
            <AlertCircle className="h-3.5 w-3.5" /> {t("manual.error_title")}
          </p>
          <p className="text-[#6B7280] truncate" title={lastErr.error}>{lastErr.error}</p>
        </div>
      ) : last ? (
        <p className="text-[10px] text-[#1B2E7A] mb-2 inline-flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> {t("manual.ok_ready")}
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        {info.files && info.files.length > 0 ? (
          info.files.map((f) => (
            <a
              key={f.filename}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-between gap-2 bg-white hover:bg-[#FFFFFF] border border-[#E5E7EB] hover:border-[#1B2E7A] px-3 py-2 rounded-md text-xs transition-colors"
              data-testid={`download-manual-${f.filename}`}
            >
              <span className="inline-flex items-center gap-2 text-[#1A1A1A]">
                <Download className="h-3.5 w-3.5 text-[#1B2E7A]" />
                <span className="font-medium">{f.label}</span>
              </span>
              <span className="text-[10px] text-[#6B7280]">{f.size_kb} KB</span>
            </a>
          ))
        ) : null}
        <button
          onClick={rebuild}
          disabled={busy || !!inProg}
          className="inline-flex items-center justify-center gap-1.5 bg-[#1B2E7A] hover:bg-[#13205A] text-white px-3 py-2 rounded-md text-xs font-medium transition-colors disabled:opacity-50 mt-1"
          data-testid="rebuild-manual-btn"
          title={t("manual.rebuild_btn_title")}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy || inProg ? "animate-spin" : ""}`} />
          {inProg ? t("manual.generating") : t("manual.rebuild_btn", { lang: pdfLocale.toUpperCase() })}
        </button>
      </div>
    </div>
  );
}
